import blossom from "edmonds-blossom";
import { uid } from "uid";
import { POINTS_FOR_WIN } from "../../constants";
import calculatePoints from "../calculatePoints";

// --- Main Export ---

export const generateSwissRound = (teams, matches = {}, schedule) => {
  const roundsPlayed = schedule ? Object.keys(schedule).length : 0;
  const isFirstRound = Object.keys(matches).length === 0;

  const { idToIndex, indexToId, possibleOpponents } = buildTeamIndexMaps(
    teams,
    matches,
  );

  const allExhausted = Object.values(possibleOpponents).every(
    (ops) => ops.length === 0,
  );
  if (allExhausted) {
    return { newMatches: null, newRound: null };
  }

  // Pre-compute stats once per team rather than re-scanning matches for every edge
  const teamStatsMap = buildTeamStatsMap(teams, matches);

  const graph = buildWeightedGraph(
    possibleOpponents,
    teamStatsMap,
    idToIndex,
    isFirstRound,
  );
  const pairedResults = blossom(graph, true);

  const newMatches = buildMatchesFromPairings(pairedResults, indexToId);
  const gameIds = Object.keys(newMatches);

  gameIds.sort((a, b) =>
    sortByMaxRest(a, b, newMatches, matches, schedule, roundsPlayed),
  );

  return {
    newMatches,
    newRound: { [roundsPlayed]: gameIds },
  };
};

// --- Team Index Map Builder ---

/**
 * Builds bidirectional index maps and calculates valid opponents for each team,
 * excluding teams they've already faced.
 */
const buildTeamIndexMaps = (teams, matches) => {
  const idToIndex = {};
  const indexToId = {};
  const possibleOpponents = {};

  Object.keys(teams).forEach((teamId, index) => {
    idToIndex[teamId] = index;
    indexToId[index] = teamId;

    const opponentsAlreadyFaced = getOpponentsFaced(teamId, matches);

    possibleOpponents[teamId] = Object.keys(teams).filter(
      (opponentId) =>
        opponentId !== teamId && !opponentsAlreadyFaced.includes(opponentId),
    );
  });

  return { idToIndex, indexToId, possibleOpponents };
};

const getOpponentsFaced = (teamId, matches) => {
  if (!matches) return [];

  return Object.values(matches).reduce((faced, match) => {
    if (match.team1Id === teamId) faced.push(match.team2Id);
    else if (match.team2Id === teamId) faced.push(match.team1Id);
    return faced;
  }, []);
};

const getTeamStats = (teamId, matches) => {
  return Object.values(matches).reduce(
    (stats, match) => {
      const isTeam1 = match.team1Id === teamId;
      const isTeam2 = match.team2Id === teamId;

      if (!isTeam1 && !isTeam2) return stats;

      const ownScore = isTeam1 ? match.team1Score : match.team2Score;
      const theirScore = isTeam1 ? match.team2Score : match.team1Score;

      return {
        wins: stats.wins + (ownScore > theirScore ? 1 : 0),
        draws: stats.draws + (ownScore === theirScore ? 1 : 0),
        goalsFor: stats.goalsFor + ownScore,
        goalsAgainst: stats.goalsAgainst + theirScore,
      };
    },
    { wins: 0, draws: 0, goalsFor: 0, goalsAgainst: 0 },
  );
};

const buildTeamStatsMap = (teams, matches) => {
  return Object.keys(teams).reduce((map, teamId) => {
    map[teamId] = getTeamStats(teamId, matches);
    return map;
  }, {});
};

const buildWeightedGraph = (
  possibleOpponents,
  teamStatsMap,
  idToIndex,
  isFirstRound,
) => {
  const edges = [];
  const seen = new Set();

  for (const [teamId, opponentIds] of Object.entries(possibleOpponents)) {
    for (const opponentId of opponentIds) {
      const edgeKey = [idToIndex[teamId], idToIndex[opponentId]]
        .sort()
        .join("-");
      if (seen.has(edgeKey)) continue;
      seen.add(edgeKey);

      edges.push([
        idToIndex[teamId],
        idToIndex[opponentId],
        calcMatchupWeight(teamId, opponentId, teamStatsMap, isFirstRound),
      ]);
    }
  }

  return edges;
};

const buildMatchesFromPairings = (pairedResults, indexToId) => {
  const newMatches = {};

  pairedResults.forEach((pairedIndex, teamIndex) => {
    if (pairedIndex === -1 || pairedIndex >= teamIndex) return;

    const gameId = uid();
    newMatches[gameId] = {
      team1Id: indexToId[pairedIndex],
      team2Id: indexToId[teamIndex],
    };
  });

  return newMatches;
};

const sortByMaxRest = (
  gameIdA,
  gameIdB,
  newMatches,
  matches,
  schedule,
  roundsPlayed,
) => {
  if (!roundsPlayed) return 0;

  const lastPlayedTime = (teamId) => {
    for (let round = roundsPlayed - 1; round >= 0; round--) {
      const gameIndex = schedule[round].findIndex(
        (matchId) =>
          matches[matchId].team1Id === teamId ||
          matches[matchId].team2Id === teamId,
      );
      if (gameIndex !== -1) return [round, gameIndex];
    }
    return null;
  };

  const leastRestedTime = (gameId) => {
    const { team1Id, team2Id } = newMatches[gameId];
    const t1 = lastPlayedTime(team1Id);
    const t2 = lastPlayedTime(team2Id);

    if (!t1 && !t2) return null;
    if (!t1) return t2;
    if (!t2) return t1;

    if (t1[0] !== t2[0]) return t1[0] > t2[0] ? t1 : t2;
    return t1[1] > t2[1] ? t1 : t2;
  };

  const timeA = leastRestedTime(gameIdA);
  const timeB = leastRestedTime(gameIdB);

  if (!timeA && !timeB) return 0;
  if (!timeA) return -1;
  if (!timeB) return 1;

  if (timeA[0] !== timeB[0]) return timeA[0] - timeB[0];
  return timeA[1] - timeB[1];
};

const calcMatchupWeight = (teamId, opponentId, teamStatsMap, isFirstRound) => {
  const teamStrength = (id) => {
    if (isFirstRound) return Math.floor(Math.random() * 100);

    const { wins, draws, goalsFor, goalsAgainst } = teamStatsMap[id];
    const points = calculatePoints(wins, draws, POINTS_FOR_WIN);
    const goalDifferential = goalsFor - goalsAgainst;
    const calcGoalDiff = goalDifferential >= 0 ? goalDifferential : 0;
    const strength = points * 10 + calcGoalDiff;

    return strength || 1;
  };

  const score = Math.abs(teamStrength(teamId) - teamStrength(opponentId));
  const weight = score === 0 ? -1 : -score;

  return weight;
};
